import React from 'react';
import { NxTheme, withAlpha } from './theme';
import { NxIcon } from './Icons';
import { BRAND, Org } from '../../config/branding';
export function OrgLogo({ org, size = 32, onDark }: { org: Org; size?: number; onDark?: boolean }) {
  return (
    <img
      src={org.logo}
      alt={`${org.short} logo`}
      style={{
        width: size, height: size, flexShrink: 0,
        objectFit: 'contain', boxSizing: 'border-box',
        borderRadius: '50%',
        background: onDark ? '#ffffff' : 'transparent',
        padding: onDark ? size * 0.06 : 0,
      }}
    />
  );
}

// ── Wordmark ──
export function Wordmark({ t, compact, color }: { t: NxTheme; compact?: boolean; color?: string }) {
  const fg = color || t.ink;
  const sub = color ? withAlpha(color, 0.62) : t.inkMute;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <OrgLogo org={BRAND.primary} size={compact ? 28 : 34} onDark={Boolean(color)} />
      <div className="nx-wordmark-text" style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
        <span style={{
          fontFamily: '"Geist", ui-sans-serif, sans-serif',
          fontWeight: 600, fontSize: compact ? 13 : 15,
          letterSpacing: '0.01em', color: fg,
        }}>{BRAND.primary.short}</span>
        {!compact && (
          <span style={{
            fontFamily: '"Geist", ui-sans-serif, sans-serif',
            fontSize: 10.5, color: sub, letterSpacing: '0.01em',
          }}>{BRAND.primary.name}</span>
        )}
      </div>
    </div>
  );
}

// ── Field ──
interface FieldProps {
  t: NxTheme;
  label: string;
  name?: string;
  value?: string;
  placeholder?: string;
  type?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  options?: { value: string; label: string }[];
  helper?: React.ReactNode;
  error?: boolean;
  w?: string | number;
  disabled?: boolean;
  leading?: React.ReactNode;
  trailing?: React.ReactNode;
  required?: boolean;
}

export function NxField({ t, label, name, value, placeholder, type = 'text', onChange, options, helper, error, w, disabled, leading, trailing, required }: FieldProps) {
  const labelStyle: React.CSSProperties = {
    display: 'block', fontSize: 11, fontWeight: 600,
    letterSpacing: '0.05em', textTransform: 'uppercase',
    color: t.inkMute, marginBottom: 6,
  };
  const inputStyle: React.CSSProperties = {
    width: '100%', height: 44,
    padding: `0 14px 0 ${leading ? 38 : 14}px`,
    boxSizing: 'border-box', background: t.surface,
    border: `1px solid ${error ? t.danger : t.border}`,
    borderRadius: 8, fontFamily: 'inherit', fontSize: 14,
    color: t.ink, outline: 'none',
  };

  let control;
  if (options) {
    control = (
      <select
        name={name}
        value={value}
        onChange={onChange as any}
        disabled={disabled}
        style={{ ...inputStyle, appearance: 'none', cursor: 'pointer', paddingRight: 32 }}
      >
        <option value="">{placeholder || 'Select...'}</option>
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    );
  } else {
    control = (
      <input
        type={type}
        name={name}
        value={value}
        placeholder={placeholder}
        onChange={onChange as any}
        disabled={disabled}
        style={{ ...inputStyle, paddingRight: trailing ? 44 : 14 }}
      />
    );
  }

  return (
    <div style={{ width: w || '100%' }}>
      <label style={labelStyle}>{label}{required && <span style={{ color: t.danger, marginLeft: 3 }}>*</span>}</label>
      <div style={{ position: 'relative' }}>
        {control}
        {leading && (
          <div style={{
            position: 'absolute', top: 0, left: 0, height: 44,
            display: 'flex', alignItems: 'center', paddingLeft: 13,
            color: t.inkMute, pointerEvents: 'none',
          }}>
            {leading}
          </div>
        )}
        {trailing && (
          <div style={{
            position: 'absolute', top: 0, right: 0, height: 44,
            display: 'flex', alignItems: 'center', paddingRight: 6,
          }}>
            {trailing}
          </div>
        )}
      </div>
      {helper && <div style={{ fontSize: 12, color: error ? t.danger : t.inkMute, marginTop: 6 }}>{helper}</div>}
    </div>
  );
}

// ── Button ──
interface ButtonProps {
  t: NxTheme;
  kind?: 'primary' | 'secondary' | 'ghost';
  children: React.ReactNode;
  w?: string | number;
  icon?: React.ReactNode;
  danger?: boolean;
  onClick?: (e: React.MouseEvent) => void;
  disabled?: boolean;
  type?: 'button' | 'submit';
}

export function NxButton({ t, kind = 'primary', children, w, icon, danger, onClick, disabled, type = 'button' }: ButtonProps) {
  const base: React.CSSProperties = {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    gap: 8, height: 44, padding: '0 18px', borderRadius: 8,
    fontFamily: 'inherit', fontSize: 14, fontWeight: 500,
    letterSpacing: '-0.005em', cursor: disabled ? 'not-allowed' : 'pointer',
    border: '1px solid transparent', width: w,
    whiteSpace: 'nowrap', opacity: disabled ? 0.5 : 1,
  };

  let style: React.CSSProperties;
  if (kind === 'primary') {
    style = { ...base, background: danger ? t.danger : t.accent, color: t.accentInk };
  } else if (kind === 'secondary') {
    style = { ...base, background: 'transparent', color: t.ink, borderColor: t.borderStrong };
  } else {
    style = { ...base, background: 'transparent', color: t.inkSoft };
  }

  return <button type={type} style={style} onClick={onClick} disabled={disabled}>{icon}{children}</button>;
}

// ── Pill ──
type PillTone = 'live' | 'enrolled' | 'notenrolled' | 'paid' | 'neutral' | 'danger' | 'warn';

export function Pill({ t, tone = 'neutral', children }: { t: NxTheme; tone?: PillTone; children: React.ReactNode }) {
  const tones: Record<string, { bg: string; fg: string; dot?: string }> = {
    live: { bg: withAlpha(t.success, t.dark ? 0.18 : 0.12), fg: t.success, dot: t.success },
    enrolled: { bg: withAlpha(t.accent, t.dark ? 0.18 : 0.10), fg: t.accent, dot: t.accent },
    notenrolled: { bg: t.paperAlt, fg: t.inkSoft, dot: t.inkMute },
    paid: { bg: withAlpha(t.success, t.dark ? 0.18 : 0.12), fg: t.success },
    neutral: { bg: t.paperAlt, fg: t.inkSoft },
    danger: { bg: withAlpha(t.danger, 0.12), fg: t.danger },
    warn: { bg: withAlpha(t.warning, 0.14), fg: t.warning },
  };
  const c = tones[tone] || tones.neutral;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      height: 22, padding: '0 9px', borderRadius: 999,
      background: c.bg, color: c.fg,
      fontSize: 11, fontWeight: 500, letterSpacing: '0.02em', whiteSpace: 'nowrap',
    }}>
      {c.dot && <span style={{ width: 5, height: 5, borderRadius: 99, background: c.dot }} />}
      {children}
    </span>
  );
}

// ── SupportNote ── (help & issues contact, shown on the auth pages)
export function SupportNote({ t, onDark }: { t: NxTheme; onDark?: boolean }) {
  const ink = onDark ? '#ffffff' : t.ink;
  const label = onDark ? withAlpha(ink, 0.5) : t.inkMute;
  const body = onDark ? withAlpha(ink, 0.78) : t.inkSoft;
  const s = BRAND.support;
  return (
    <div style={{
      background: onDark ? withAlpha(ink, 0.05) : t.surface,
      border: `1px solid ${onDark ? withAlpha(ink, 0.1) : t.border}`,
      borderRadius: 10, padding: '12px 14px',
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 7,
        fontSize: 10, fontWeight: 600, letterSpacing: '0.08em',
        textTransform: 'uppercase', color: label,
      }}>
        {NxIcon.help(label, 13)} Help &amp; Issues
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 16px', marginTop: 8, fontSize: 12, color: body }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          {NxIcon.user(label, 12)} {s.name}
        </span>
        <a href={`tel:${s.phone}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: body, textDecoration: 'none' }}>
          {NxIcon.phone(label, 12)} {s.phone}
        </a>
        <a href={`mailto:${s.email}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: body, textDecoration: 'none' }}>
          {NxIcon.mail(label, 12)} {s.email}
        </a>
      </div>
    </div>
  );
}

// ── FormSection ── (circled icon + rule heading above a group of fields)
export function FormSection({ t, icon, title, children }: {
  t: NxTheme; icon: React.ReactNode; title: string; children?: React.ReactNode;
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '26px 0 16px' }}>
      <span style={{
        width: 26, height: 26, borderRadius: 8, flexShrink: 0,
        background: t.accent, color: t.accentInk,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      }}>{icon}</span>
      <span style={{
        fontSize: 11.5, fontWeight: 600, letterSpacing: '0.08em',
        textTransform: 'uppercase', color: t.ink, whiteSpace: 'nowrap',
      }}>{title}</span>
      <span style={{ flex: 1, height: 1, background: t.border }} />
      {children}
    </div>
  );
}

// ── BrandPanel ── (deep navy marketing panel for login/signup/reset)
export function BrandPanel({ t }: { t: NxTheme }) {
  const ink = '#ffffff';
  const highlight = '#9aa8ff';
  const card = withAlpha(ink, 0.06);
  const cardBorder = withAlpha(ink, 0.1);

  return (
    <div className="nx-brand" style={{
      position: 'relative', width: '100%', height: '100%',
      background: 'linear-gradient(158deg, #0c1130 0%, #141d47 45%, #1d2a63 100%)',
      color: ink, overflow: 'hidden',
      display: 'flex', flexDirection: 'column', boxSizing: 'border-box',
    }}>
      <div style={{
        position: 'absolute', inset: 0,
        backgroundImage: `radial-gradient(${withAlpha(ink, 0.06)} 1px, transparent 1px)`,
        backgroundSize: '24px 24px',
        maskImage: 'linear-gradient(150deg, black 20%, transparent 75%)',
        WebkitMaskImage: 'linear-gradient(150deg, black 20%, transparent 75%)',
      }} />
      <div style={{
        position: 'absolute', left: '-18%', top: '22%',
        width: 460, height: 460, borderRadius: '50%',
        border: `1px solid ${withAlpha(ink, 0.05)}`,
        background: `radial-gradient(circle, ${withAlpha(ink, 0.05)} 0%, transparent 62%)`,
      }} />
      <div style={{
        position: 'absolute', right: '-24%', top: '-14%',
        width: 520, height: 520, borderRadius: '50%',
        background: `radial-gradient(circle, ${withAlpha(highlight, 0.14)} 0%, transparent 60%)`,
      }} />

      <div className="nx-brand-inner" style={{
        position: 'relative', flex: 1, minHeight: 0, overflowY: 'auto',
        display: 'flex', flexDirection: 'column', padding: '36px 40px 30px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexShrink: 0 }}>
          {BRAND.logos.map(org => <OrgLogo key={org.short} org={org} size={54} onDark />)}
          <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.3 }}>
            <span style={{ fontSize: 19, fontWeight: 700, letterSpacing: '0.03em', color: ink }}>
              {BRAND.primary.short}
            </span>
            <span style={{ fontSize: 11.5, color: withAlpha(ink, 0.6) }}>
              {BRAND.primary.name}
            </span>
          </div>
        </div>

        <div className="nx-brand-gap" style={{ flex: 1, minHeight: 32 }} />

        <div className="nx-brand-body" style={{ maxWidth: 440, flexShrink: 0 }}>
          <span className="nx-brand-badge" style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            padding: '7px 14px', borderRadius: 999,
            background: card, border: `1px solid ${cardBorder}`,
            fontSize: 10.5, fontWeight: 600, letterSpacing: '0.1em',
            textTransform: 'uppercase', color: withAlpha(ink, 0.82),
          }}>
            {NxIcon.award(highlight, 13)} {BRAND.badge}
          </span>

          <h2 className="nx-brand-title" style={{
            margin: '22px 0 0', fontWeight: 700,
            fontSize: 42, lineHeight: 1.12, letterSpacing: '-0.02em',
          }}>
            <span style={{ display: 'block', color: ink }}>{BRAND.headline.lead}</span>
            <span style={{ display: 'block', color: highlight }}>{BRAND.headline.highlight}</span>
          </h2>

          <p className="nx-brand-desc" style={{ margin: '18px 0 0', fontSize: 13.5, lineHeight: 1.65, color: withAlpha(ink, 0.62) }}>
            {BRAND.description}
          </p>

          <div className="nx-brand-institute" style={{
            display: 'flex', alignItems: 'center', gap: 13, marginTop: 26,
            padding: '14px 16px', borderRadius: 12,
            background: card, border: `1px solid ${cardBorder}`,
          }}>
            <span style={{
              width: 34, height: 34, borderRadius: 10, flexShrink: 0,
              background: withAlpha(highlight, 0.16), color: highlight,
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            }}>{NxIcon.cap(highlight, 17)}</span>
            <div style={{ lineHeight: 1.35 }}>
              <div style={{ fontSize: 12.5, fontWeight: 600, color: ink }}>{BRAND.secondary.name}</div>
              <div style={{ fontSize: 11, color: withAlpha(ink, 0.55), marginTop: 2 }}>{BRAND.instituteMeta}</div>
            </div>
          </div>

          <div className="nx-brand-support" style={{ marginTop: 12 }}>
            <SupportNote t={t} onDark />
          </div>
        </div>

        <div className="nx-brand-gap" style={{ flex: 1, minHeight: 24 }} />

        <div className="nx-brand-foot" style={{ fontSize: 10.5, lineHeight: 1.6, color: withAlpha(ink, 0.38), flexShrink: 0 }}>
          {BRAND.copyright}
          <br />
          {BRAND.copyrightSub}
        </div>
      </div>
    </div>
  );
}

// ── PageShell ──
export function PageShell({ t, children, style }: { t: NxTheme; children: React.ReactNode; style?: React.CSSProperties }) {
  return (
    <div style={{
      width: '100%', height: '100%',
      background: t.paper, color: t.ink,
      fontFamily: '"Geist", ui-sans-serif, system-ui, -apple-system, sans-serif',
      ...style,
    }}>
      {children}
    </div>
  );
}

// ── TopBar ──
interface CrumbItem {
  label: string;
  path?: string;
}

interface TopBarProps {
  t: NxTheme;
  crumbs?: (string | CrumbItem)[];
  right?: React.ReactNode;
  dense?: boolean;
  onNavigate?: (path: string) => void;
}

export function TopBar({ t, crumbs, right, dense, onNavigate }: TopBarProps) {
  return (
    <div className="nx-topbar" style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      height: dense ? 56 : 64, padding: '0 28px',
      borderBottom: `1px solid ${t.border}`,
      background: t.surface, flexShrink: 0,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 18, minWidth: 0 }}>
        <Wordmark t={t} />
        {crumbs && (
          <div className="nx-topbar-crumbs" style={{ display: 'flex', alignItems: 'center', gap: 10, marginLeft: 6 }}>
            <span style={{ color: t.inkFaint, fontSize: 14 }}>/</span>
            {crumbs.map((c, i) => {
              const label = typeof c === 'string' ? c : c.label;
              const path = typeof c === 'string' ? undefined : c.path;
              const isLast = i === crumbs.length - 1;
              const isClickable = !isLast && path && onNavigate;
              return (
                <React.Fragment key={i}>
                  <span
                    onClick={isClickable ? () => onNavigate(path) : undefined}
                    style={{
                      fontSize: 13,
                      color: isLast ? t.ink : t.inkSoft,
                      fontWeight: isLast ? 500 : 400,
                      cursor: isClickable ? 'pointer' : 'default',
                      textDecoration: 'none',
                    }}
                  >{label}</span>
                  {!isLast && <span style={{ color: t.inkFaint, fontSize: 14 }}>/</span>}
                </React.Fragment>
              );
            })}
          </div>
        )}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexShrink: 0 }}>
        {right}
      </div>
    </div>
  );
}

export function UserChip({ t, name = 'User', email, regNo, onLogout, onProfile }: { t: NxTheme; name?: string; email?: string; regNo?: string; onLogout?: () => void; onProfile?: () => void }) {
  const [open, setOpen] = React.useState(false);
  const [showConfirm, setShowConfirm] = React.useState(false);
  const ref = React.useRef<HTMLDivElement>(null);
  const menuRef = React.useRef<HTMLDivElement>(null);
  const [menuShift, setMenuShift] = React.useState(0);
  const initials = name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();

  React.useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // The menu hangs off the chip's right edge, which pushes it past the left of
  // the screen when the chip sits near it. Nudge it back into view once open.
  React.useLayoutEffect(() => {
    if (!open || !menuRef.current) {
      setMenuShift(0);
      return;
    }
    const gutter = 10;
    const r = menuRef.current.getBoundingClientRect();
    if (r.left < gutter) setMenuShift(gutter - r.left);
    else if (r.right > window.innerWidth - gutter) setMenuShift(window.innerWidth - gutter - r.right);
    else setMenuShift(0);
  }, [open]);

  return (
    <>
      <div ref={ref} style={{ position: 'relative' }}>
        <div
          style={{
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '4px 10px 4px 4px', borderRadius: 99,
            border: `1px solid ${t.border}`, cursor: 'pointer',
          }}
          onClick={() => setOpen(o => !o)}
        >
          <div style={{
            width: 28, height: 28, borderRadius: 99,
            background: t.paperAlt, color: t.inkSoft,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 11, fontWeight: 600,
          }}>{initials}</div>
          <div className="nx-userchip-meta" style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.1, minWidth: 0 }}>
            <span style={{ fontSize: 12, fontWeight: 500, color: t.ink, whiteSpace: 'nowrap' }}>{name}</span>
            {email && <span style={{ fontSize: 11, color: t.inkMute, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 180 }}>{email}</span>}
          </div>
          {NxIcon.chevronDown(t.inkMute)}
        </div>

        {open && (
          <div ref={menuRef} style={{
            position: 'absolute', top: 'calc(100% + 6px)', right: 0,
            minWidth: 180, maxWidth: 'calc(100vw - 20px)', background: t.surface,
            border: `1px solid ${t.border}`, borderRadius: 10,
            boxShadow: '0 4px 16px rgba(0,0,0,0.10)', zIndex: 100,
            padding: '6px 0', overflow: 'hidden',
            transform: menuShift ? `translateX(${menuShift}px)` : undefined,
          }}>
            <div style={{ padding: '10px 14px', borderBottom: `1px solid ${t.border}` }}>
              <div style={{ fontSize: 12, fontWeight: 500, color: t.ink }}>{name}</div>
              {email && <div style={{ fontSize: 11, color: t.inkMute, marginTop: 2, overflowWrap: 'anywhere' }}>{email}</div>}
              {regNo && (
                <div style={{ fontSize: 10, color: t.inkMute, marginTop: 6, letterSpacing: '0.06em', textTransform: 'uppercase', fontWeight: 600 }}>
                  Reg No.
                </div>
              )}
              {regNo && <div style={{ fontSize: 12, color: t.ink, fontWeight: 500, overflowWrap: 'anywhere' }}>{regNo}</div>}
            </div>
            {onProfile && (
              <button
                onClick={() => { setOpen(false); onProfile(); }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  width: '100%', padding: '10px 14px', border: 'none',
                  background: 'transparent', cursor: 'pointer',
                  fontSize: 13, color: t.ink, fontFamily: 'inherit',
                  textAlign: 'left',
                }}
                onMouseEnter={e => (e.currentTarget.style.background = t.paperAlt)}
                onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
              >
                {NxIcon.doc(t.inkSoft)} My Profile
              </button>
            )}
            <button
              onClick={() => { setOpen(false); setShowConfirm(true); }}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                width: '100%', padding: '10px 14px', border: 'none',
                background: 'transparent', cursor: 'pointer',
                fontSize: 13, color: t.danger, fontFamily: 'inherit',
                textAlign: 'left',
              }}
              onMouseEnter={e => (e.currentTarget.style.background = t.paperAlt)}
              onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
            >
              {NxIcon.logout(t.danger)} Logout
            </button>
          </div>
        )}
      </div>

      {/* Logout confirmation modal */}
      {showConfirm && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 9999,
          background: 'rgba(0,0,0,0.4)', display: 'flex',
          alignItems: 'center', justifyContent: 'center',
          fontFamily: '"Geist", ui-sans-serif, system-ui, -apple-system, sans-serif',
        }} onClick={() => setShowConfirm(false)}>
          <div
            style={{
              background: t.surface, borderRadius: 14,
              padding: '28px 32px', maxWidth: 380, width: '90%',
              boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
            }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ fontSize: 16, fontWeight: 600, color: t.ink, marginBottom: 8 }}>
              Confirm Logout
            </div>
            <div style={{ fontSize: 13, color: t.inkSoft, lineHeight: 1.5, marginBottom: 24 }}>
              Are you sure you want to logout? Any unsaved progress may be lost.
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <NxButton t={t} kind="secondary" onClick={() => setShowConfirm(false)}>Cancel</NxButton>
              <NxButton t={t} kind="primary" danger onClick={() => { setShowConfirm(false); onLogout?.(); }}>Logout</NxButton>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ── PaletteSwatch ── (for instructions page)
export function PaletteSwatch({ kind, t }: { kind: 'unanswered' | 'answered' | 'review' | 'both'; t: NxTheme }) {
  const sw: Record<string, { bg: string; border: string; fg: string }> = {
    unanswered: { bg: t.paperAlt, border: t.borderStrong, fg: t.inkSoft },
    answered: { bg: withAlpha(t.success, 0.12), border: t.success, fg: t.success },
    review: { bg: withAlpha(t.warning, 0.14), border: t.warning, fg: t.warning },
    both: { bg: withAlpha(t.accent, 0.12), border: t.accent, fg: t.accent },
  };
  const s = sw[kind];
  return (
    <div style={{
      width: 28, height: 28, borderRadius: 6,
      background: s.bg, border: `1.25px solid ${s.border}`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 12, fontWeight: 600, color: s.fg,
      fontFamily: '"Geist Mono", ui-monospace, monospace',
    }}>5</div>
  );
}

// ── PaletteBtn ── (for exam sidebar)
export function PaletteBtn({ status, n, t, active, onClick, hasNotification }: {
  status: 'unanswered' | 'answered' | 'review' | 'both' | 'approved' | 'remarked';
  n: number; t: NxTheme; active?: boolean; onClick?: () => void; hasNotification?: boolean;
}) {
  const sw: Record<string, { bg: string; fg: string; border: string }> = {
    unanswered: { bg: t.dark ? t.surfaceAlt : '#f0eee9', fg: t.inkSoft, border: t.border },
    answered: { bg: withAlpha('#0d6efd', t.dark ? 0.25 : 0.12), fg: '#0d6efd', border: withAlpha('#0d6efd', 0.4) },
    review: { bg: withAlpha(t.warning, t.dark ? 0.22 : 0.16), fg: t.warning, border: withAlpha(t.warning, 0.35) },
    both: { bg: withAlpha(t.accent, t.dark ? 0.25 : 0.14), fg: t.accent, border: withAlpha(t.accent, 0.4) },
    approved: { bg: withAlpha(t.success, t.dark ? 0.22 : 0.14), fg: t.success, border: withAlpha(t.success, 0.35) },
    remarked: { bg: withAlpha('#dc3545', t.dark ? 0.22 : 0.12), fg: '#dc3545', border: withAlpha('#dc3545', 0.35) },
  };
  const s = sw[status];
  return (
    <button
      onClick={onClick}
      style={{
        width: 36, height: 36, borderRadius: 8,
        background: s.bg, color: s.fg,
        border: `${active ? 1.5 : 1}px solid ${active ? t.ink : s.border}`,
        fontFamily: '"Geist Mono", ui-monospace, monospace',
        fontSize: 13, fontWeight: 500, cursor: 'pointer',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        position: 'relative',
        boxShadow: active ? `0 0 0 3px ${withAlpha(t.ink, 0.06)}` : 'none',
      }}
    >
      {n}
      {(status === 'review' || status === 'both') && (
        <span style={{
          position: 'absolute', top: 3, right: 3,
          width: 5, height: 5, borderRadius: 99,
          background: status === 'review' ? t.warning : t.accent,
        }} />
      )}
      {hasNotification && (
        <span style={{
          position: 'absolute', top: -3, right: -3,
          width: 8, height: 8, borderRadius: 99,
          background: '#e53e3e',
          border: '1.5px solid white',
        }} />
      )}
    </button>
  );
}
